import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";

import { DropDownList } from "@progress/kendo-react-dropdowns";
import { PDFViewerTool } from "@progress/kendo-react-pdf-viewer";
import {
  ItemRenderProps,
  TreeView,
  TreeViewCheckChangeEvent,
  TreeViewCheckChangeSettings,
  TreeViewCheckDescriptor,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent,
  TreeViewOperationDescriptor,
  handleTreeViewCheckChange,
} from "@progress/kendo-react-treeview";
import { debounce } from "lodash";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseIcon,
  BringToFront,
  CheckIcon,
  ClipboardPaste,
  Component,
  Copy,
  CopyMinus,
  CopyPlus,
  Download,
  FileText,
  Folder,
  FolderGit,
  FolderGit2,
  FolderKanban,
  History,
  Import,
  Layers,
  List,
  MoreHorizontal,
  Paperclip,
  Plus,
  SquarePen,
  SquarePlus,
  TableProperties,
  Trash2Icon,
  Ungroup,
  User,
  X,
} from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomLabel from "../../component/global/RequiredMark";
import SearchBox from "../../component/ui/SearchBox";
import DeletionReasons from "../../constants/deletionReasons";
import { IBBS } from "../../interfaces/bbs.interface";
import {
  IMember,
  IMemberDetail,
  IMemberLine,
} from "../../interfaces/member.interface";
import {
  IProject,
  IShopDrawings,
  Level,
} from "../../interfaces/project.interface";
import { ISearchFilter } from "../../interfaces/searchFilter.interface";
import bbsSvc from "../../services/bbs.service";
import memberSvc from "../../services/member.service";
import projectSvc from "../../services/project.service";
import { CustomDialogBox } from "../../shared-components/dialog-box";
import {
  resetFetchProjects,
  resetMemberLines,
  setSelectedProject,
  triggerFetchProjects
} from "../../store/features/commonSlice";
import { setToast } from "../../store/features/toastSlice";
import {
  clearCopiedUnits,
  clearSelectedUnits,
  setCopiedUnits,
} from "../../store/features/unitsSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { RootState } from "../../store/store/store";
import { getDialogInfo } from "../../utils/dialog.utils";
import { PROJECT_ADMIN, PROJECT_CONTRIBUTOR, SUPER_USER } from "../../utils/roles.constant";
import { hasProjectDeletionPermission, isAuthorize } from "../../utils/utils";
import ProjectAuditLogsDrawer from "./ProjectAuditLogsDrawer";

const tools: PDFViewerTool[] = [
  "pager",
  "spacer",
  "zoomInOut",
  //"zoom",
  "selection",
  "spacer",
  "search",
  "print",
  "download",
];

type TreeViewDataItem = {
  text: string;
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  items?: TreeViewDataItem[];
  level?: number;
  levelCode?: string;
  subLevelCode?: string;
  projectId?: string;
  uniqueId?: string;
};

const AddLevelContext = createContext({
  // Project Right Click Fuctions //

  handleManageProject: (data: any, action: string) => {},
  handleSummary: (data: any, action: string) => {},
  handleProjectLogs: (data: any) => {},

  handleManageDetailingOffices: (data: any, action: string) => {},
  handleManageSubcontractors: (data: any, action: string) => {},

  // Levels Right Click Fuctions //

  // Member Right Click Fuctions //
  handleManageMemberRevision: (data: any, action: string) => {},
  handleManageMemberProperties: (data: any, action: string) => {},
  handleManageMemberImport: (data: any, action: string) => {},
  handleMemberPDFExport: (data: any, action: string) => {},
  handleMemberExcelExport: (data: any, action: string) => {},
  handleMemberAssignment: (data: any, action: string) => {},
  handleMemberParts: (data: any, action: string) => {},
  handleCopyMember: (data: any, action: string) => {},
  handlePasteMember: (data: any, action: string) => {},

  // Common Right Click Functions //
  addLevel: (data: any, action: string) => {},
  handleManageBBS: (data: any, action: string) => {},
  handleManageCallOff: (data: any, action: string) => {},

  handleCopyUnitsClick: (data: any, action: string) => {},
  handlePasteUnitsClick: (data: any, action: string) => {},
  handleDeleteUnitsClick: (data: any, action: string) => {},

  toggleMemberDeleteDialog: () => {},
  toggleUnDeleteDialog: () => {},
  toggleDeletePermanentlyDialog: () => {},
  setSelectedItem: (data: any) => {},
  handleTreeSelection: () => {},
  showManageBBS: false,
  showManageCallOff: false,
  isMemberCopied: false,
  searchValue: "",
  highlightMatchedText: (text: string, searchValue: string): React.ReactNode =>
    text as any,
});

interface IProps {
  projects: IProject[];
  setProjects: React.Dispatch<React.SetStateAction<IProject[]>>;
  setSelectedLevelBBS: React.Dispatch<React.SetStateAction<IBBS | null>>;
  memberDetail: IMemberDetail | undefined;
  setMemberDetail: React.Dispatch<
    React.SetStateAction<IMemberDetail | undefined>
  >;
  prevMemberDetail: any;
  treeView: TreeViewDataItem[];
  setTreeView: React.Dispatch<React.SetStateAction<TreeViewDataItem[]>>;
  setDistinctMembers: React.Dispatch<React.SetStateAction<IMemberLine[]>>;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  showAddProjectDrawer: boolean;
  setShowAddProjectDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showMemberPropertiesDrawer: boolean;
  setShowMemberPropertiesDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showEditMemberPropertiesDrawer: boolean;
  setShowEditMemberPropertiesDrawer: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showMemberPartsDrawer: boolean;
  setShowMemberPartsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showMemberImportDrawer: boolean;
  setShowMemberImportDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showMemberRevisionDrawer: boolean;
  setShowMemberRevisionDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  memberImportType: any;
  setMemberImportType: React.Dispatch<React.SetStateAction<any | null>>;
  selected: boolean;
  toggleSelected: () => void;
  showEditProjectDrawer: boolean;
  setShowEditProjectDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showAddDetailingOfficeDrawer: boolean;
  setShowAddDetailingOfficeDrawer: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showAddSubcontractorDrawer: boolean;
  setShowAddSubcontractorDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  projectToEdit: (IProject & { id: string }) | null;
  setProjectToEdit: React.Dispatch<
    React.SetStateAction<(IProject & { id: string }) | null>
  >;
  prevProject: (IProject & { id: string }) | null;
  setPrevProject: React.Dispatch<
    React.SetStateAction<(IProject & { id: string }) | null>
  >;
  showManageBBS: boolean;
  setShowManageBBS: React.Dispatch<React.SetStateAction<boolean>>;
  showManageCallOff: boolean;
  setShowManageCallOff: React.Dispatch<React.SetStateAction<boolean>>;

  memberProperties: any;
  setMemberProperties: React.Dispatch<React.SetStateAction<any | null>>;
  showMemberAssignmentsDrawer: boolean;
  setShowMemberAssignmentsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showMembersView: boolean;
  setShowMembersView: React.Dispatch<React.SetStateAction<boolean>>;
  copiedItems: any;
  setCopiedItems: any;
  pastedItems: any;
  setPastedItems: any;
  check: string[] | TreeViewCheckDescriptor;
  setCheck: React.Dispatch<
    React.SetStateAction<string[] | TreeViewCheckDescriptor>
  >;
  select: string[];
  setSelect: React.Dispatch<React.SetStateAction<string[]>>;
  expand: TreeViewOperationDescriptor;
  setExpand: React.Dispatch<React.SetStateAction<TreeViewOperationDescriptor>>;
  processTreeViewItems: any;
  isPasted: boolean;
  setIsPasted: React.Dispatch<React.SetStateAction<boolean>>;
}

const TreeViewMain = ({
  projects,
  setProjects,
  memberDetail,
  setMemberDetail,
  prevMemberDetail,
  setSelectedLevelBBS,
  treeView,
  setTreeView,
  setDistinctMembers,
  expanded,
  setExpanded,
  showAddProjectDrawer,
  setShowAddProjectDrawer,
  showMemberPropertiesDrawer,
  setShowMemberPropertiesDrawer,
  showEditMemberPropertiesDrawer,
  setShowEditMemberPropertiesDrawer,
  showMemberPartsDrawer,
  setShowMemberPartsDrawer,
  showMemberImportDrawer,
  setShowMemberImportDrawer,
  showMemberRevisionDrawer,
  setShowMemberRevisionDrawer,
  memberImportType,
  setMemberImportType,
  selected,
  toggleSelected,
  showEditProjectDrawer,
  setShowEditProjectDrawer,
  showAddDetailingOfficeDrawer,
  setShowAddDetailingOfficeDrawer,
  showAddSubcontractorDrawer,
  setShowAddSubcontractorDrawer,
  projectToEdit,
  setProjectToEdit,
  prevProject,
  setPrevProject,
  showManageBBS,
  setShowManageBBS,
  showManageCallOff,
  setShowManageCallOff,
  memberProperties,
  setMemberProperties,
  showMemberAssignmentsDrawer,
  setShowMemberAssignmentsDrawer,
  showMembersView,
  setShowMembersView,
  copiedItems,
  setCopiedItems,
  pastedItems,
  setPastedItems,
  check,
  setCheck,
  select,
  setSelect,
  expand,
  setExpand,
  processTreeViewItems,
  isPasted,
  setIsPasted,
}: IProps) => {
  const debouncedSearch = useRef(
    debounce((value: string) => fetchProjects(value), 500)
  ).current;

  const commonState = useAppSelector((state: RootState) => state.common);
  const selectedUnits = useAppSelector(
    (state: RootState) => state.units.selectedUnits
  );
  const copiedUnits = useAppSelector(
    (state: RootState) => state.units.copiedUnits
  );

  const dispatch = useAppDispatch();

  const [isMemberCopied, setIsMemberCopied] = useState(false);
  const [copiedMember, setCopiedMember] = useState<any>(null);
  const [pastedMember, setPastedMember] = useState<any>(null);

  const [showPasteConfirmation, setShowPasteConfimation] = useState(false);

  const selectedLevel = useRef<any>(null);
  const selectedAction = useRef<any>("");
  const [showDialog, setShowDialog] = useState(false);
  const [inputVal, setInputVal] = useState<string>("");

  const [searchValue, setSearchValue] = useState("");

  const [modalVisible, setModalVisible] = useState(false);
  const [unDeleteModalVisible, setUnDeleteModalVisible] = useState(false);
  const [memberDeleteModalVisible, setMemberDeleteModalVisible] =
    useState(false);
  const [deletePermanentlyModalVisible, setDeletePermanentlyModalVisible] =
    useState(false);

  const [deletionReason, setDeletionReason] = useState("");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [fileNameVal, setFileNameVal] = useState<string>("");
  const [showAuditLogsDrawer, setShowAuditLogsDrawer] = useState(false);
  const [selectedProjectForAuditLogs, setSelectedProjectForAuditLogs] =
    useState<any>(null);

  // Search state setup
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(-1);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [itemsToExpand, setItemsToExpand] = useState<string[]>([]);

  // Updated search function that works with filtered trees
  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    if (!value || value.trim() === "") {
      console.log("Clearing search");
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setIsSearching(false);
      setItemsToExpand([]);
      return;
    }

    console.log(`Searching for: "${value}"`);
    setIsSearching(true);

    // Search all visible items in the filtered tree
    const results: any[] = [];
    const toExpand: string[] = [];

    // Function to search the visible tree
    const searchVisibleTree = (
      items: TreeViewDataItem[],
      path: string[] = []
    ) => {
      if (!items) return;

      items.forEach((item) => {
        // Get the display text without code
        const displayText = item.text?.split("-Code-")[0] || "";

        // Check if it matches the search
        if (displayText.toLowerCase().includes(value.toLowerCase())) {
          console.log(`Match found: "${displayText}" (${item.uniqueId})`);

          // Store the result with its unique ID
          results.push({
            item: { ...item },
            uniqueId: item.uniqueId,
            displayText,
            path: [...path, item.text],
          });
        }

        // Always check children
        if (item.items && item.items.length > 0) {
          // Add parent to expand list
          toExpand.push(item.text);
          // Continue search in children
          searchVisibleTree(item.items, [...path, item.text]);
        }
      });
    };

    // Start search with the currently filtered tree
    searchVisibleTree(filteredTreeView);

    console.log(
      `Found ${results.length} results, need to expand ${toExpand.length} items`
    );
    setSearchResults(results);
    setItemsToExpand(toExpand);

    // Navigate to first result if available
    if (results.length > 0) {
      setCurrentSearchIndex(0);
      setTimeout(() => {
        navigateToSearchResult(results[0]);
      }, 50);
    } else {
      setCurrentSearchIndex(-1);
    }
  };

  // Navigation that works with both filtered and unfiltered trees
  const navigateToSearchResult = (result: any) => {
    if (!result || !result.uniqueId) {
      console.log("Invalid search result", result);
      return;
    }

    console.log(`Navigating to: ${result.displayText} (${result.uniqueId})`);

    // 1. First, ensure all needed items are expanded
    const expandPath = result.path.slice(0, -1); // All except the last item

    if (expandPath.length > 0) {
      console.log(`Expanding path: ${expandPath.join(" > ")}`);

      // Create new expanded array that includes all required items
      const expandedIds = [...(expand.ids || [])];

      expandPath.forEach((id: any) => {
        if (!expandedIds.includes(id)) {
          expandedIds.push(id);
        }
      });

      // Set expansion state
      console.log(`Setting ${expandedIds.length} items as expanded`);
      setExpand({ ids: expandedIds, idField: "text" });
    }

    // 2. Find the item in the filtered tree by its unique ID
    const findItemByUniqueId = (
      items: TreeViewDataItem[],
      uniqueId: string
    ): [TreeViewDataItem | null, string] => {
      if (!items) return [null, ""];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.uniqueId === uniqueId) {
          return [item, `${i}`]; // Return the item and its current index
        }

        if (item.items && item.items.length > 0) {
          const [found, childIndex] = findItemByUniqueId(item.items, uniqueId);
          if (found) {
            return [found, `${i}_${childIndex}`]; // Build hierarchical index
          }
        }
      }

      return [null, ""];
    };

    // 3. Short delay to ensure expansion completes
    setTimeout(() => {
      // Find the item in the current filtered tree view
      const [foundItem, hierarchicalIndex] = findItemByUniqueId(
        filteredTreeView,
        result.uniqueId
      );

      if (foundItem && hierarchicalIndex) {
        console.log(`Found item at index ${hierarchicalIndex}`);

        // Set the selection
        setSelect([hierarchicalIndex]);

        // Update selected level
        selectedLevel.current = foundItem;

        // 4. Trigger the click
        setTimeout(() => {
          // Create click event
          const clickEvent = {
            item: foundItem,
            itemHierarchicalIndex: hierarchicalIndex,
            nativeEvent: {
              target: {
                className:
                  "self-center cursor-pointer hover:text-primaryMain flex items-center gap-1 flex-1 project-text min-w-0",
              },
              stopPropagation: () => {},
              preventDefault: () => {},
            },
          };

          // Directly call click handler
          console.log("Triggering click event");
          onItemClick(clickEvent as any);

          // 5. Scroll to the element
          setTimeout(() => {
            // Create a reliable selector for both filtered and unfiltered views
            const selector = `[data-unique-id="${result.uniqueId}"]`;
            console.log(`Looking for element: ${selector}`);

            const element = document.querySelector(selector);
            if (element) {
              console.log("Scrolling to element");
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
              console.log("Element not found in DOM");
            }
          }, 100);
        }, 100);
      } else {
        console.error(`Could not find item with uniqueId: ${result.uniqueId}`);
      }
    }, 100);
  };

  // Navigation functions
  const goToNextSearchResult = () => {
    console.log(
      `Next: Current index ${currentSearchIndex}, total ${searchResults.length}`
    );

    if (!searchResults.length) return;

    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    console.log(`Moving to result ${nextIndex} of ${searchResults.length}`);

    setCurrentSearchIndex(nextIndex);
    navigateToSearchResult(searchResults[nextIndex]);
  };

  const goToPreviousSearchResult = () => {
    console.log(
      `Previous: Current index ${currentSearchIndex}, total ${searchResults.length}`
    );

    if (!searchResults.length) return;

    const prevIndex =
      (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    console.log(`Moving to result ${prevIndex} of ${searchResults.length}`);

    setCurrentSearchIndex(prevIndex);
    navigateToSearchResult(searchResults[prevIndex]);
  };

  // Update the TreeViewItemCell component to highlight matched text
  // Add this at the beginning of the TreeViewItemCell component
  const highlightMatchedText = (text: string, searchValue: string) => {
    if (!searchValue || searchValue.trim() === "") {
      return text;
    }

    // Escape special regex characters in the search value
    const escapedSearchValue = searchValue.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    const regex = new RegExp(`(${escapedSearchValue})`, "gi");
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 text-black font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  const showCheckboxes = () => {
    if (
      commonState?.selectedCallOff &&
      commonState.selectedCallOff?.status === "Released"
    ) {
      return false;
    }

    if (showManageBBS || showManageCallOff) {
      return true;
    }

    return false;
  };

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    const ids: string[] = expand.ids ? expand.ids.slice() : [];
    const index: number = ids.indexOf(event.item.text);

    index === -1 ? ids.push(event.item.text) : ids.splice(index, 1);
    setExpand({ ids, idField: "text" });
  };

  const handleCollapseAll = () => {
    setExpand({ ids: [], idField: "text" });
  };

  const handleExpandAll = () => {
    if (
      commonState?.selectedProject?.event?.item &&
      commonState?.selectedProject?.event?.item?.level === 0
    ) {
      const selectedItem = commonState.selectedProject.event.item;
      const textsToExpand: string[] = [];

      textsToExpand.push(selectedItem.text);

      const collectTexts = (items: TreeViewDataItem[] | undefined) => {
        if (!items) return;

        items.forEach((item) => {
          textsToExpand.push(item.text);
          if (item.items) {
            collectTexts(item.items);
          }
        });
      };

      if (selectedItem.items) {
        collectTexts(selectedItem.items);
      }

      setExpand({ ids: textsToExpand, idField: "text" });

      return;
    }
    const allTexts = treeView.flatMap((item: TreeViewDataItem) => {
      return [
        item.text,
        ...(item.items
          ? item.items.flatMap((subItem) => {
              return [
                subItem.text,
                ...(subItem.items
                  ? subItem.items.flatMap((subSubItem) => {
                      return [
                        subSubItem.text,

                        ...(subSubItem.items
                          ? subSubItem.items.flatMap((subSubSubItem) => {
                              return [
                                subSubSubItem.text,

                                ...(subSubSubItem.items
                                  ? subSubSubItem.items.flatMap(
                                      (subSubSubSubItem) => {
                                        return subSubSubSubItem.text;
                                      }
                                    )
                                  : []),
                              ];
                            })
                          : []),
                      ];
                    })
                  : []),
              ];
            })
          : []),
      ];
    });
    setExpand({ ids: allTexts, idField: "text" });
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    const className =
      typeof event.nativeEvent.target.className === "string"
        ? event.nativeEvent.target.className
        : event.nativeEvent.target.className?.baseVal || "";

    if (
      className &&
      (className.includes("pasteBtn") || className.includes("copyBtn"))
    ) {
      return;
    }
    if (
      className ===
      "k-button k-button-md k-button-flat k-button-flat-base k-rounded-md w-full iconBtn px-1 border-b border-neutral-20"
    ) {
      return;
    }
    if (
      className ===
      "k-button k-button-md k-button-flat k-button-flat-base k-rounded-md p-2 bg-none outline-none k-ripple-target"
    ) {
      return;
    }
    if (
      className !== "" &&
      className !==
        "overflow-hidden text-ellipsis whitespace-break-spaces flex flex-wrap max-w-[284px]" &&
      className !== "flex items-center gap-x-2 group flex-1 w-full min-w-0"
    ) {
      console.log("Clicked on:", className);
      return;
    }

    if (showManageCallOff) return; //&& event.item.level === 4 || event.item.level === 5;

    if (
      showManageBBS &&
      (event.item.level === 3 ||
        event.item.level === 4 ||
        event.item.level === 5)
    )
      return;

    setSelect([event.itemHierarchicalIndex]);
    selectedLevel.current = event.item;
    const settings: TreeViewCheckChangeSettings = {
      singleMode: false,
      checkChildren: false,
      checkParents: false,
    };
    const eventData = {
      item: event.item,
      itemHierarchicalIndex: event.itemHierarchicalIndex,
    };

    if (
      eventData.itemHierarchicalIndex ===
      commonState?.selectedProject?.event?.itemHierarchicalIndex
    ) {
      // dispatch(setSelectedProject(null));
    } else {
      dispatch(
        setSelectedProject({
          event: eventData,
          check,
          treeView,
          settings,
          index: eventData.itemHierarchicalIndex,
        })
      );
    }

    if (event.item.level === 4 || event.item.level === 5) {
      memberSvc.getByMemberId(event.item.id).then((res) => {
        if (res.data) {
          setMemberDetail(res.data);
          prevMemberDetail.current = JSON.stringify(res.data);
        }
      });
    }
  };

  const onCheckChange = (event: TreeViewCheckChangeEvent) => {
    if (
      event.item.level === 0 ||
      event.item.level === 1 ||
      event.item.level === 2 ||
      event.item.level === 3 ||
      !event.item.isActive ||
      (showManageBBS && event.item.level === 5) ||
      event.item.status === "Released"
    )
      return;

    if (!event.nativeEvent.target.closest(".k-treeview")) {
      if (event.nativeEvent.code === "Space") return;
    }

    const settings: TreeViewCheckChangeSettings = {
      singleMode: false,
      checkChildren: false,
      checkParents: false,
    };
    const eventData = {
      item: event.item,
      itemHierarchicalIndex: event.itemHierarchicalIndex,
    };

    setCheck(
      handleTreeViewCheckChange(eventData as any, check, treeView, settings)
    );
  };

  useEffect(() => {
    if (
      copiedMember === null &&
      pastedMember === null &&
      !commonState.selectedProject
    ) {
      setSelect([""]);
      return;
    }
    setSelect([commonState.selectedProject.event.itemHierarchicalIndex]);
  }, [commonState.selectedProject]);

  useEffect(() => {
    if (commonState?.fetchProjects) {
      fetchProjects();
      dispatch(resetFetchProjects());
    }
  }, [commonState?.fetchProjects]);

  useEffect(() => {
    if (commonState?.fetchMemberLines) {
      fetchMemberLines();
    }
  }, [commonState?.fetchMemberLines]);

  const fetchProjects = useCallback(
    (searchValue?: string) => {
      let searchFilters: ISearchFilter[] = [];
      if (searchValue) {
        searchFilters = [
          { field: "name", operator: "contains", value: searchValue },
        ];
      }
      projectSvc.getTree().then((res) => {
        if (res.data) {
          const treeViewData = mapProjectsToTreeView(res.data);
          setTreeView(treeViewData);
          setProjects(res.data);
          setExpand((prev) => {
            return { ...prev };
          });
        }
      });
    },
    [setProjects]
  );

  const fetchMemberLines = () => {
    if (
      commonState?.selectedProject?.event?.item &&
      (commonState?.selectedProject?.event?.item?.level === 4 ||
        commonState?.selectedProject?.event?.item?.level === 5)
    ) {
      memberSvc
        .getByMemberId(commonState?.selectedProject?.event.item.id)
        .then((res) => {
          if (res.data) {
            setMemberDetail(res.data);
            prevMemberDetail.current = JSON.stringify(res.data);
            dispatch(resetMemberLines());
          }
        });
    }
  };

  //Refreshing selecting project state
  useEffect(() => {
    if (
      projects?.length &&
      treeView?.length &&
      commonState?.selectedProject?.event?.item
    ) {
      const level = commonState?.selectedProject?.event?.item?.level;
      switch (level) {
        case 0:
          const item = treeView.find(
            (p: any) =>
              p.projectId ===
              commonState?.selectedProject?.event?.item?.projectId
          );
          if (
            item &&
            item.items?.length !==
              commonState?.selectedProject?.event?.item?.items?.length
          ) {
            let newItem = JSON.parse(
              JSON.stringify(commonState.selectedProject)
            );
            newItem.event.item = item;
            dispatch(setSelectedProject(newItem));
          }
          break;
        case 1:
          const parentItem: any = treeView.find(
            (p: any) =>
              p.projectId ===
              commonState?.selectedProject?.event?.item?.projectId
          );
          if (parentItem) {
            const item = parentItem.items.find(
              (l: any) =>
                l.levelCode ===
                commonState?.selectedProject?.event?.item?.levelCode
            );
            if (
              item &&
              item.items?.length !==
                commonState?.selectedProject?.event?.item?.items?.length
            ) {
              let newItem = JSON.parse(
                JSON.stringify(commonState.selectedProject)
              );
              newItem.event.item = item;
              dispatch(setSelectedProject(newItem));
            }
          }
          break;
      }
    }
  }, [treeView, projects, commonState?.selectedProject]);

  const prevProjectsLength = useRef(0);

  // Memoize markStatus to avoid recalculating on every render
  const markStatus = useCallback(
    (member: IMember, shopDwg: IShopDrawings, project: any) => {
      // First check memberStatus field - backend provides actual status including call-off info
      if (member.memberStatus) {
        const status = member.memberStatus.toLowerCase();
        if (
          status.includes("called off") ||
          status.includes("calledoff") ||
          status.includes("call-off")
        ) {
          return "Called Off";
        }
        if (status === "released") {
          return "Released";
        }
        if (status === "partially released") {
          return "Partially Released";
        }
      }

      // Check if member has any lines that have been called off
      const hasCalledOffLines =
        member.lines &&
        member.lines.length > 0 &&
        member.lines.some(
          (line) =>
            line.callOffId && line.callOffId !== null && line.callOffId !== ""
        );
      const allLinesCalledOff =
        member.lines &&
        member.lines.length > 0 &&
        member.lines.every(
          (line) =>
            line.callOffId && line.callOffId !== null && line.callOffId !== ""
        );

      // If all lines are called off, mark as "Called Off"
      if (allLinesCalledOff) {
        return "Called Off";
      }

      // If some lines are called off, mark as "Partially Called Off"
      if (hasCalledOffLines) {
        return "Partially Called Off";
      }

      // Check if any members are released (this might indicate they're in a call-off)
      // If member has membersReleased > 0, it could mean it's been called off
      return member.membersReleased == 0 &&
        shopDwg.memberParts?.filter((x) => x.parentMemberPartId == member.id)
          ?.length === 0
        ? "Engineered"
        : (member.membersReleased > 0 &&
            member.membersEngineered == member.membersReleased) ||
          member.membersReleased > member.membersEngineered ||
          (shopDwg.memberParts?.filter(
            (x) => x.parentMemberPartId === member.id
          ).length > 0 &&
            shopDwg.memberParts
              ?.filter((x) => x.parentMemberPartId === member.id)
              .every((part) => part.noOfMem === part.membersReleased))
        ? "Released"
        : member.membersReleased > 0 && member.membersReleased != member.noOfMem
        ? "Partially Released"
        : "Engineered";
    },
    []
  );

  const mapProjectsToTreeView = (projects: IProject[]): TreeViewDataItem[] => {
    return projects.map((project, projectIdx) => ({
      text: project.name + "-Code-" + project.code,
      projectAbbreviation: project.abbreviation,
      callOffPrefix: project.callOffPrefix,
      level: 0,
      projectId: project.id,
      projectName: project.name,
      standardId: project.standardId,
      detailingOffices: project.detailingOffices,
      subcontractors: project.subcontractors,
      barLength: project.barLength,
      barType: project.barTypeCode,
      index: `${projectIdx}`,
      // Add a unique ID for each node
      uniqueId: `project_${project.id}`,
      items: project.levels.map((level, levelIdx) => ({
        text: level.name + "-Code-" + level.code,
        level: 1,
        projectId: project.id,
        idx: levelIdx,
        barLength: project.barLength,
        barType: project.barTypeCode,

        standardId: project.standardId,
        levelCode: level.code,
        levelName: level.name,
        createdOn: level.createdOn,
        projectName: project.name,
        index: `${projectIdx}_${levelIdx}`,
        isActive: level.isActive,
        // Add unique ID for level
        uniqueId: `level_${project.id}_${level.code}`,
        items: level.subLevels.map((subLevel, subLevelIdx) => ({
          text: subLevel.name + "-Code-" + subLevel.code,
          level: 2,
          projectId: project.id,
          parentId: levelIdx,
          idx: subLevelIdx,
          barLength: project.barLength,
          barType: project.barTypeCode,

          standardId: project.standardId,
          subLevelCode: subLevel.code,
          subLevelName: subLevel.name,
          levelCode: level.code,
          levelName: level.name,
          projectName: project.name,
          projectAbbreviation: project.abbreviation,
          callOffPrefix: project.callOffPrefix,
          isActive: subLevel.isActive,
          parentIsActive: level.isActive,

          createdOn: subLevel.createdOn,
          index: `${projectIdx}_${levelIdx}_${subLevelIdx}`,
          // Add unique ID for sublevel
          uniqueId: `sublevel_${project.id}_${level.code}_${subLevel.code}`,

          items: subLevel.shopDrawings.map((shopDwg, shopDwgIdx) => {
            // Calculate status for shop drawing based on all members
            const activeMembers = shopDwg.members.filter(
              (x) => x != null && (project.showDeleted || (x && x.isActive))
            );

            const memberStatuses = activeMembers.map((member) =>
              markStatus(member, shopDwg, project)
            );

            const allReleased = activeMembers.length > 0 &&
              memberStatuses.every((status) => status === "Released");

            const shopDrawingStatus = allReleased ? "Released" : undefined;
            if(project.name === "Fixing Error Issue" && subLevel.name === "E2" && shopDwg.name === "WAMI-ALEC-SDW-ST-900011"){
              console.log("Shop Drawing Status:", shopDwg.name, shopDrawingStatus);
            }

            return {
              text: shopDwg.name + "-Code-" + shopDwg.code,
              level: 3,
              projectId: project.id,
              parentId: subLevelIdx,
              idx: shopDwgIdx,
              levelId: levelIdx,
              subLevelId: subLevelIdx,
              barLength: project.barLength,
              barType: project.barTypeCode,

              standardId: project.standardId,
              subLevelCode: subLevel.code,
              subLevelName: subLevel.name,
              shopDrawingCode: shopDwg.code,
              shopDrawingName: shopDwg.name,
              levelCode: level.code,
              levelName: level.name,
              projectName: project.name,
              projectAbbreviation: project.abbreviation,
              callOffPrefix: project.callOffPrefix,
              index: `${projectIdx}_${levelIdx}_${subLevelIdx}_${shopDwgIdx}`,
              isActive: shopDwg.isActive,
              status: shopDrawingStatus,

              createdOn: shopDwg.createdOn,
              // Add unique ID for shopdrawing
              uniqueId: `shopdrawing_${project.id}_${level.code}_${subLevel.code}_${shopDwg.code}`,
              allMembers: shopDwg.members
                .filter(
                  (x) => x != null && (project.showDeleted || (x && x.isActive))
                )
                .map((member, shopDrawingIdx) => {
                  return member;
                }),
              items: shopDwg.members
                .filter(
                  (x) => x != null && (project.showDeleted || (x && x.isActive))
                )
                .map((member, memberIdx) => ({
                id: member.id,
                memberId: member.id,
                memberName: member.name,
                code: member.id,
                text: member.name,
                levelId: levelIdx,
                status: markStatus(member, shopDwg, project),
                level: 4,
                projectName: project.name,
                projectId: project.id,
                projectAbbreviation: project.abbreviation,
                callOffPrefix: project.callOffPrefix,
                parentId: shopDwgIdx,
                shopDwgId: shopDwgIdx,
                subLevelId: subLevelIdx,
                idx: memberIdx,
                barLength: project.barLength,
                barType: project.barTypeCode,

                standardId: project.standardId,
                levelCode: level.code,
                levelName: level.name,
                subLevelCode: subLevel.code,
                subLevelName: subLevel.name,
                shopDrawingCode: shopDwg.code,
                shopDrawingName: shopDwg.name,
                isActive: member.isActive,
                noOfMem: member.noOfMem,
                superseded: member.superseded,
                index: `${projectIdx}_${levelIdx}_${subLevelIdx}_${shopDwgIdx}_${memberIdx}`,
                // Add unique ID for members
                uniqueId: `member_${project.id}_${level.code}_${subLevel.code}_${shopDwg.code}_${member.id}`,

                items: shopDwg.memberParts
                  ?.filter((x) => x.parentMemberPartId == member.id)
                  .map((memberPart, memberPartIdx) => ({
                    id: memberPart.id,
                    memberId: member.id,
                    memberName: member.name,
                    memberPartId: memberPart.id,
                    code: memberPart.id,
                    text: memberPart.name,

                    status:
                      memberPart.membersReleased == 0
                        ? "Engineered"
                        : (memberPart.membersReleased > 0 &&
                            memberPart.noOfMem == memberPart.membersReleased) ||
                          memberPart.membersReleased > memberPart.noOfMem
                        ? "Released"
                        : "Partially Released",

                    levelId: levelIdx,
                    level: 5,
                    projectName: project.name,
                    projectId: project.id,
                    projectAbbreviation: project.abbreviation,
                    callOffPrefix: project.callOffPrefix,
                    index: `${projectIdx}_${levelIdx}_${subLevelIdx}_${shopDwgIdx}_${memberIdx}_${memberPartIdx}`,
                    parentId: levelIdx,
                    subLevelId: subLevelIdx,
                    parentMemberId: memberIdx,
                    idx: memberPartIdx,
                    barLength: project.barLength,
                    barType: project.barTypeCode,

                    standardId: project.standardId,
                    levelCode: level.code,
                    levelName: level.name,
                    subLevelCode: subLevel.code,
                    subLevelName: subLevel.name,
                    shopDrawingCode: shopDwg.code,
                    shopDrawingName: shopDwg.name,
                    isActive: memberPart.isActive,
                    noOfMem: member.noOfMem,
                    isDisabled: false,
                    // Add unique ID for member parts
                    uniqueId: `memberPart_${project.id}_${level.code}_${subLevel.code}_${shopDwg.code}_${member.id}_${memberPart.id}`,
                  })),
              })),
            };
          }),
        })),
      })),
    }));
  };

  // Memoize tree view to prevent unnecessary recalculations
  const memoizedTreeView = useMemo(() => {
    return mapProjectsToTreeView(projects);
  }, [projects, markStatus]);

  useEffect(() => {
    setTreeView(memoizedTreeView);
    if (projects.length && prevProjectsLength.current !== projects.length) {
      prevProjectsLength.current = projects.length;
    }
  }, [memoizedTreeView]);

  const addLevel = (data: any, action: string) => {
    if (action === "edit") {
      setInputVal(data.text?.split("-Code-")[0]);
    }
    selectedLevel.current = data;
    selectedAction.current = action;
    if (action !== "delete") {
      toggleDialog();
    } else {
      toggleDeleteDialog();
    }
  };

  const handleDeleteDialog = (action: string) => {
    if (action === "yes") {
      deleteLevel();
    }
    toggleDeleteDialog();
  };

  const toggleDeleteDialog = () => {
    setModalVisible(!modalVisible);
  };
  const handleMemberDeleteDialog = (action: string) => {
    if (action === "yes") {
      deleteLevel();
    }
    toggleMemberDeleteDialog();
  };

  const toggleUnDeleteDialog = () => {
    setUnDeleteModalVisible(!unDeleteModalVisible);
  };

  const toggleMemberDeleteDialog = () => {
    setMemberDeleteModalVisible(!memberDeleteModalVisible);
  };

  const toggleDeletePermanentlyDialog = () => {
    setDeletePermanentlyModalVisible(!deletePermanentlyModalVisible);
  };

  const handleDeletePermanentlyDialog = (action: string) => {
    if (action === "yes") {
      deletePermanently();
    }
    toggleDeletePermanentlyDialog();
  };

  const handleUndeleteDialog = (action: string) => {
    if (action === "yes") {
      if (selectedLevel.current.level === 1) {
        // Undelete Level
        unDeleteLevel();
      } else if (selectedLevel.current.level === 2) {
        // Undelete SubLevel
        unDeleteSubLevel();
      } else if (selectedLevel.current.level === 3) {
        unDeleteShopDrawing();
      } else if (selectedLevel.current.level === 4) {
        unDeleteMember();
      }
    }
    toggleUnDeleteDialog();
  };

  const setSelectedItem = (data: any) => {
    const idx = treeView.findIndex((x: any) => x.projectId === data?.projectId);
    if (idx === -1) return;

    var index = "";

    switch (data.level) {
      case 0:
        index = idx.toString();
        break;
      case 1:
        index = idx.toString() + "_" + data.idx;
        break;
      case 2:
        index =
          idx.toString() +
          "_" +
          data.parentId.toString() +
          "_" +
          data.idx.toString();
        break;
      case 3:
        index =
          idx.toString() +
          "_" +
          data.levelId.toString() +
          "_" +
          data.parentId.toString() +
          "_" +
          data.idx.toString();
        break;
    }

    setSelect([index]);
    selectedLevel.current = data;
  };

  const handleTreeSelection = () => {
    const settings: TreeViewCheckChangeSettings = {
      singleMode: false,
      checkChildren: false,
      checkParents: false,
    };
    const eventData = {
      item: selectedLevel.current,
      itemHierarchicalIndex: select.length ? select[0] : 0,
    };

    if (
      eventData.itemHierarchicalIndex ===
      commonState?.selectedProject?.event?.itemHierarchicalIndex
    ) {
      // dispatch(setSelectedProject(null));
    } else {
      dispatch(
        setSelectedProject({
          event: eventData,
          check,
          treeView,
          settings,
          index: eventData.itemHierarchicalIndex,
        })
      );
    }
  };

  //// --------- Project Right Click Functions  ---------- ///

  const handleManageProject = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setPrevProject(proj);
    setShowEditProjectDrawer(!showEditProjectDrawer);
  };

  const handleSummary = async (data: any, action: string) => {
    const res = await projectSvc.exportSummary(
      data.projectId,
      data.projectName,
      action,
      data.levelCode
    );
  };

  const handleProjectLogs = (data: any) => {
    setSelectedProjectForAuditLogs(data);
    setShowAuditLogsDrawer(true);
  };

  const handleManageDetailingOffices = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setShowAddDetailingOfficeDrawer(!showAddDetailingOfficeDrawer);
  };
  const handleManageSubcontractors = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setShowAddSubcontractorDrawer(!showAddSubcontractorDrawer);
  };

  //// --------- Project Right Click Functions  ---------- ///

  //// --------- Common Right Click Functions  ---------- ///

  const handleManageBBS = (data: any, action: string) => {
    setShowManageBBS(true);
    setShowMembersView(false);
  };
  const handleManageCallOff = (data: any, action: string) => {
    // dispatch(setSelectedMenu(commonState?.selected));
    // dispatch(setManageCallOff(commonState.manageCallOff));
    setShowManageCallOff(true);
    setShowMembersView(false);
  };

  //// --------- Common Right Click Functions  ---------- ///

  //// --------- Member Right Click Functions  ---------- ///

  const handleManageMemberProperties = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setMemberProperties(data);
    if (data.memberId)
      setShowEditMemberPropertiesDrawer(!showEditMemberPropertiesDrawer);
    else setShowMemberPropertiesDrawer(!showMemberPropertiesDrawer);
  };

  const handleManageMemberRevision = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setMemberProperties(data);
    setShowMemberRevisionDrawer(!showMemberRevisionDrawer);
  };

  const handleManageMemberImport = (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setMemberProperties(data);
    setMemberImportType(action);
    setShowMemberImportDrawer(!showMemberImportDrawer);
  };

  const handleMemberPDFExport = async (data: any, action: string) => {
    const res = await bbsSvc.exportMemberPDF(data.id);
    if (res.data) {
      if (res.data) {
        setFileUrl(res.data);
        setShowPdfPreview(true);
        setFileNameVal(data.name);
      }
    }
  };

  const handleMemberExcelExport = async (data: any, action: string) => {
    const res = await bbsSvc.exportMemberLines(data.id, data.name);
  };

  const handleMemberAssignment = async (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setMemberProperties(data);
    setShowMemberAssignmentsDrawer(!showMemberAssignmentsDrawer);
  };

  const handleMemberParts = async (data: any, action: string) => {
    const proj: any = projects?.find((p) => p.id === data.projectId);
    setProjectToEdit(proj);
    setMemberProperties(data);
    setShowMemberPartsDrawer(!showMemberPartsDrawer);
  };

  const handleCopyMember = async (data: any, action: string) => {
    // const proj: any = projects?.find((p) => p.id === data.projectId);
    // setProjectToEdit(proj);
    setCopiedMember(data);
    setIsMemberCopied(true);

    dispatch(
      setToast({
        toastMsg: "Member copied successfully",
        toastType: "success",
      })
    );
    return;
  };

  const handlePasteMember = async (data: any, action: string) => {
    if (!isMemberCopied) {
      dispatch(
        setToast({
          toastMsg: "No member copied",
          toastType: "error",
        })
      );
    }
    setIsPasted(!isPasted);
    setPastedMember(data);
    setShowPasteConfimation(!showPasteConfirmation);
    return;
  };

  const handleCopyUnitsClick = async (data: any, action: string) => {
    const memberDetails: any = await memberSvc.getByMemberId(data.id);
    if (memberDetails?.data) {
      const selectedUnitsToCopy = memberDetails.data.memberLines.filter(
        (line: IMemberLine) => selectedUnits[line.id!]
      );

      const unitsToCopy = selectedUnitsToCopy.map((line: IMemberLine) => ({
        ...line,
        id: `Copy-${line.id}`,
        mark: `Copy-${line.mark}`,
        selected: false,
        pasted: true,
      }));
      dispatch(setCopiedUnits(unitsToCopy));
      dispatch(
        setToast({
          toastMsg: "Units copied successfully",
          toastType: "success",
        })
      );

      memberSvc.getByMemberId(data.id).then((res) => {
        if (res.data) {
          setMemberDetail(res.data);
          prevMemberDetail.current = JSON.stringify(res.data);
        }
      });
    }
  };

  const handlePasteUnitsClick = async (data: any, action: string) => {
    if (copiedUnits.length > 0) {
      const memberDetails: any = await memberSvc.getByMemberId(data.id);

      if (memberDetails?.data) {
        const updatedMemberDetails = {
          ...memberDetails.data,
          memberLines: [...memberDetails.data.memberLines, ...copiedUnits],
        };

        // Update member with new units
        const response = await memberSvc.updateMemberDetail(
          data.id,
          updatedMemberDetails
        );
        if (!response.error) {
          dispatch(
            setToast({
              toastMsg: "Units pasted successfully",
              toastType: "success",
            })
          );
          dispatch(clearCopiedUnits());
          dispatch(triggerFetchProjects());

          memberSvc.getByMemberId(data.id).then((res) => {
            if (res.data) {
              setMemberDetail(res.data);
              prevMemberDetail.current = JSON.stringify(res.data);
            }
          });
        }
      }
    }
  };

  const handleDeleteUnitsClick = async (data: any, action: string) => {
    const memberDetails: any = await memberSvc.getByMemberId(data.id);

    if (memberDetails?.data) {
      const unitsToDelete = memberDetails.data.memberLines.filter(
        (line: IMemberLine) => selectedUnits[line.id!]
      );

      if (unitsToDelete.length > 0) {
        await memberSvc.deleteMemberLines(data.id, unitsToDelete);
        dispatch(
          setToast({
            toastMsg: "Units deleted successfully",
            toastType: "success",
          })
        );
        dispatch(clearSelectedUnits());
        dispatch(triggerFetchProjects());

        memberSvc.getByMemberId(data.id).then((res) => {
          if (res.data) {
            setMemberDetail(res.data);
            prevMemberDetail.current = JSON.stringify(res.data);
          }
        });
      }
    }
  };

  //// --------- Member Right Click Functions  ---------- ///

  const toggleDialog = useCallback(() => {
    setShowDialog((prevShowDialog) => !prevShowDialog);
  }, []);

  const deleteLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (!proj) return;

    let response;

    if (selectedLevel.current.level === 1) {
      // Delete Level
      response = await projectSvc.toggleActiveStatus(
        proj.id,
        false,
        selectedLevel.current.levelCode
      );
    } else if (selectedLevel.current.level === 2) {
      // Delete SubLevel
      response = await projectSvc.toggleActiveStatus(
        proj.id,
        false,
        selectedLevel.current.levelCode,
        selectedLevel.current.subLevelCode
      );
    } else if (selectedLevel.current.level === 3) {
      // Delete ShopDrawing
      response = await projectSvc.toggleActiveStatus(
        proj.id,
        false,
        selectedLevel.current.levelCode,
        selectedLevel.current.subLevelCode,
        selectedLevel.current.shopDrawingCode
      );
    } else if (selectedLevel.current.level === 4) {
      // Delete Member
      response = await projectSvc.toggleActiveStatus(
        proj.id,
        false,
        undefined,
        undefined,
        undefined,
        selectedLevel.current.id
      );
    }

    if (response && !response.error) {
      dispatch(
        setToast({
          toastType: "success",
          toastMsg: "Deleted successfully",
        })
      );

      fetchProjects();

      const item = treeView.find(
        (p: any) =>
          p.projectId === commonState?.selectedProject?.event?.item?.projectId
      );
      if (
        item &&
        item.items?.length !==
          commonState?.selectedProject?.event?.item?.items?.length
      ) {
        let newItem = JSON.parse(JSON.stringify(commonState.selectedProject));
        newItem.event.item = item;
        dispatch(setSelectedProject(newItem));
        setSelect([commonState?.selectedProject?.index.split("_")[0]]);
        setDeletionReason("");
      }
    } else if (response) {
      dispatch(
        setToast({
          toastType: "error",
          toastMsg: response.error?.message || "Failed to delete",
        })
      );
    }
  };

  const deletePermanently = async () => {
    if (selectedLevel.current.level === 3) {
      // Permanently delete shop drawing (BBS)
      try {
        const response = await bbsSvc.deleteByCode(
          selectedLevel.current.projectId,
          selectedLevel.current.levelCode,
          selectedLevel.current.subLevelCode,
          selectedLevel.current.shopDrawingCode
        );

        if (!response.error) {
          dispatch(
            setToast({
              toastType: "success",
              toastMsg:
                (response.data as any)?.message || "BBS deleted permanently.",
            })
          );
          fetchProjects();

          const item = treeView.find(
            (p: any) =>
              p.projectId ===
              commonState?.selectedProject?.event?.item?.projectId
          );
          if (
            item &&
            item.items?.length !==
              commonState?.selectedProject?.event?.item?.items?.length
          ) {
            let newItem = JSON.parse(
              JSON.stringify(commonState.selectedProject)
            );
            newItem.event.item = item;
            dispatch(setSelectedProject(newItem));
            setSelect([commonState?.selectedProject?.index.split("_")[0]]);
          }
        } else {
          if (
            response.status === 400 &&
            response.errorCode === "BBS_HAS_CALLOFFS"
          ) {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg:
                  "Cannot delete BBS. Call-Off records exist for this BBS.",
              })
            );
          } else if (response.status === 404) {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg: "BBS not found.",
              })
            );
          } else {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg:
                  (response.error as any)?.message || "Failed to delete BBS.",
              })
            );
          }
        }
      } catch (error) {
        console.error("Error deleting BBS permanently:", error);
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: "An unexpected error occurred while deleting the BBS.",
          })
        );
      }
    } else if (selectedLevel.current.level === 4) {
      // Permanently delete member
      try {
        const response = await memberSvc.delete(selectedLevel.current.id);

        if (!response.error) {
          dispatch(
            setToast({
              toastType: "success",
              toastMsg:
                (response.data as any)?.message ||
                "Member deleted permanently.",
            })
          );
          fetchProjects();

          const item = treeView.find(
            (p: any) =>
              p.projectId ===
              commonState?.selectedProject?.event?.item?.projectId
          );
          if (
            item &&
            item.items?.length !==
              commonState?.selectedProject?.event?.item?.items?.length
          ) {
            let newItem = JSON.parse(
              JSON.stringify(commonState.selectedProject)
            );
            newItem.event.item = item;
            dispatch(setSelectedProject(newItem));
            setSelect([commonState?.selectedProject?.index.split("_")[0]]);
            setDeletionReason("");
          }
        } else {
          if (
            response.status === 400 &&
            response.errorCode === "MEMBER_IN_CALLOFF"
          ) {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg:
                  "Cannot delete member. Call-Off records exist for this member.",
              })
            );
          } else if (response.status === 404) {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg: "Member not found.",
              })
            );
          } else {
            dispatch(
              setToast({
                toastType: "error",
                toastMsg:
                  (response.error as any)?.message ||
                  "Failed to delete member.",
              })
            );
          }
        }
      } catch (error) {
        console.error("Error deleting member permanently:", error);
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: "An unexpected error occurred while deleting the member.",
          })
        );
      }
    }
  };

  const unDeleteMember = async () => {
    if (commonState?.selectedProject?.event?.item) {
      const response = await projectSvc.toggleActiveStatus(
        selectedLevel.current.projectId,
        true,
        undefined,
        undefined,
        undefined,
        selectedLevel.current.id
      );

      if (!response.error) {
        dispatch(
          setToast({
            toastType: "success",
            toastMsg: "Member is Un-Deleted successfully",
          })
        );
        fetchProjects();
      } else {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: response.error?.message || "Failed to restore member",
          })
        );
      }
    }
  };

  const unDeleteLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj) {
      const response = await projectSvc.toggleActiveStatus(
        proj.id,
        true,
        selectedLevel.current.levelCode
      );

      if (!response.error) {
        dispatch(
          setToast({
            toastType: "success",
            toastMsg: "Level Un-Deleted successfully",
          })
        );
        fetchProjects();
      } else {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: response.error?.message || "Failed to restore Level",
          })
        );
      }
    }
  };

  const unDeleteSubLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj) {
      const response = await projectSvc.toggleActiveStatus(
        proj.id,
        true,
        selectedLevel.current.levelCode,
        selectedLevel.current.subLevelCode
      );

      if (!response.error) {
        dispatch(
          setToast({
            toastType: "success",
            toastMsg: "Element Un-Deleted successfully",
          })
        );
        fetchProjects();
      } else {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: response.error?.message || "Failed to restore Element",
          })
        );
      }
    }
  };

  const unDeleteShopDrawing = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj) {
      const response = await projectSvc.toggleActiveStatus(
        proj.id,
        true,
        selectedLevel.current.levelCode,
        selectedLevel.current.subLevelCode,
        selectedLevel.current.shopDrawingName
      );

      if (!response.error) {
        dispatch(
          setToast({
            toastType: "success",
            toastMsg: "BBS Un-Deleted successfully",
          })
        );
        fetchProjects();

        const item = treeView.find(
          (p: any) =>
            p.projectId === commonState?.selectedProject?.event?.item?.projectId
        );
        if (
          item &&
          item.items?.length !==
            commonState?.selectedProject?.event?.item?.items?.length
        ) {
          let newItem = JSON.parse(JSON.stringify(commonState.selectedProject));
          newItem.event.item = item;
          dispatch(setSelectedProject(newItem));
          setSelect([commonState?.selectedProject?.index.split("_")[0]]);
        }
      } else {
        dispatch(
          setToast({
            toastType: "error",
            toastMsg: response.error?.message || "Failed to restore BBS",
          })
        );
      }
    }
  };

  const handleSaveLevel = () => {
    switch (selectedAction.current) {
      case "new":
        createNewLevel();
        break;
      case "newChild":
        createNewChildLevel();
        break;
      case "newSubChild":
        createNewSubChildLevel();
        break;
      case "edit":
        editLevel();
        break;
      default:
        break;
    }
  };

  const editLevel = async () => {
    var proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj) {
      await projectSvc
        .editLevel(
          proj?.id,
          selectedLevel.current.level,
          selectedLevel.current.levelCode,
          selectedLevel.current.level === 1
            ? inputVal
            : selectedLevel.current.levelName,
          selectedLevel.current.subLevelCode,
          selectedLevel.current.level === 2
            ? inputVal
            : selectedLevel.current.subLevelName,
          selectedLevel.current.shopDrawingCode,
          selectedLevel.current.level === 3
            ? inputVal
            : selectedLevel.current.shopDrawingName,
          selectedLevel.current.id,
          selectedLevel.current.level === 4 || selectedLevel.current.level === 5
            ? inputVal
            : selectedLevel.current.text
        )
        .then((res) => {
          if (!res.error) {
            dispatch(
              setToast({
                toastType: "success",
                toastMsg: "Updated successfully",
              })
            );
            fetchProjects();
            setInputVal("");
            setShowDialog(false);
          }
        });
    }
  };

  const createNewChildLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj && selectedLevel.current.level === 1) {
      const newLevel: any = {
        name: inputVal,
        code: crypto.randomUUID(),
        subLevels: [],
      };
      proj?.levels[selectedLevel.current.idx].subLevels.push(newLevel);

      proj.levelAdded = 1;
    }
    if (proj) {
      await projectSvc.update(proj?.id, proj).then((res) => {
        if (!res.error) {
          dispatch(
            setToast({
              toastType: "success",
              toastMsg: "Element added successfully",
            })
          );
          fetchProjects();
          setInputVal("");
          setShowDialog(false);
        } else {
          proj?.levels[selectedLevel.current.idx].subLevels.pop();
        }
      });
    }
  };

  const createNewSubChildLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);
    if (proj && selectedLevel.current.level === 2) {
      const newLevel: any = {
        name: inputVal,
        code: crypto.randomUUID(),
        members: [],
      };
      proj?.levels[selectedLevel.current.parentId].subLevels[
        selectedLevel.current.idx
      ].shopDrawings.push(newLevel);

      proj.levelAdded = 2;
    }
    if (proj) {
      await projectSvc.update(proj?.id, proj).then((res) => {
        if (!res.error) {
          dispatch(
            setToast({
              toastType: "success",
              toastMsg: "Shop Drawing added successfully",
            })
          );
          fetchProjects();
          setInputVal("");
          setShowDialog(false);
        } else {
          proj?.levels[selectedLevel.current.parentId].subLevels[
            selectedLevel.current.idx
          ].shopDrawings.pop();
        }
      });
    }
  };

  const createNewLevel = async () => {
    const proj = projects.find((p) => p.id === selectedLevel.current.projectId);

    if (proj && selectedLevel.current.level === 0) {
      const newLevel: Level = {
        name: inputVal,
        code: crypto.randomUUID(),
        subLevels: [],
        isActive: true,
      };
      proj?.levels.push(newLevel);

      proj.levelAdded = 1;
    }

    if (proj) {
      await projectSvc.update(proj?.id, proj).then((res) => {
        if (!res.error) {
          dispatch(
            setToast({
              toastType: "success",
              toastMsg: "Level added successfully",
            })
          );
          fetchProjects();
          setInputVal("");
          setShowDialog(false);
        } else {
          proj?.levels.pop();
        }
      });
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // if (commonState?.selectedProject) {
    //   setCheck(
    //     handleTreeViewCheckChange(
    //       commonState.selectedProject.event,
    //       commonState.selectedProject.check,
    //       commonState.selectedProject.treeView,
    //       commonState.selectedProject.settings
    //     )
    //   );
    // }

    if (commonState?.selectedProject?.event?.item?.level === 2) {
      setDistinctMembers(
        commonState?.selectedProject?.event?.item?.allMembers || [] //.flat()
      );
    }
  }, [commonState?.selectedProject?.event?.item]);

  useEffect(() => {
    if (showAddProjectDrawer) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showAddProjectDrawer]);

  const getModalHeaderText = () => {
    if (selectedAction.current === "new") {
      if (selectedLevel.current.level === 0) {
        return "Add Level";
      }
      if (selectedLevel.current.level === 1) {
        return "Add Element";
      }
      if (selectedLevel.current.level === 2) {
        return "Add Drawing";
      }
    }

    if (selectedAction.current === "newChild") {
      return "Add Element";
    }
    if (selectedAction.current === "newSubChild") {
      return "Add Drawing";
    }

    if (selectedAction.current === "edit") {
      if (selectedLevel.current.level === 1) {
        return "Edit Level";
      }

      if (selectedLevel.current.level === 2) {
        return "Edit Element";
      }
      if (selectedLevel.current.level === 3) {
        return "Edit Drawing";
      }

      if (selectedLevel.current.level === 4) {
        return "Edit Member Name";
      }

      if (selectedLevel.current.level === 5) {
        return "Edit Member Part Name";
      }
    }
  };

  const filteredTreeView = useMemo(() => {
    let filteredProjects = projects;

    if (searchValue.trim()) {
      const lowercasedSearch = searchValue.toLowerCase();
      filteredProjects = projects.filter(
        (project) =>
          project.name.toLowerCase().includes(lowercasedSearch) ||
          project.levels.some(
            (level) =>
              level.name.toLowerCase().includes(lowercasedSearch) ||
              level.subLevels.some(
                (subLevel) =>
                  subLevel.name.toLowerCase().includes(lowercasedSearch) ||
                  subLevel.shopDrawings.some(
                    (shopDwg) =>
                      shopDwg.name.toLowerCase().includes(lowercasedSearch) ||
                      shopDwg.members.some(
                        (member) =>
                          member &&
                          member.name &&
                          member.name.toLowerCase().includes(lowercasedSearch)
                      )
                  )
              )
          )
      );
    }

    if (showManageCallOff || showManageBBS) {
      filteredProjects = projects.filter(
        (project) =>
          commonState?.selectedProject?.event?.item?.projectId == project.id
      );
    }

    var treeViewData = mapProjectsToTreeView(filteredProjects);
    setTreeView(treeViewData);

    return treeViewData;
  }, [
    commonState.selectedProject,
    projects,
    searchValue,
    showManageBBS,
    showManageCallOff,
  ]);

  const handleDownload = (e: any) => {
    handlePdfDownload();
    setShowPdfPreview(false);
    return false;
  };
  const handlePdfDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", `${fileNameVal}`);
    document.body.appendChild(link);
    link.click();
    setShowDialog(false);
    // document.removeChild(link);
  };

  const handlePasteConfimation = (action: string) => {
    if (action === "yes") {
      bbsSvc.copyMember(pastedMember, copiedMember?.id).then((res) => {
        if (!res.error) {
          setIsMemberCopied(false);
          setCopiedMember(null);
          dispatch(triggerFetchProjects());
        } else {
          dispatch(
            setToast({
              toastType: "error",
              toastMsg: res.error,
            })
          );
        }
      });
    }
    setIsPasted(!isPasted);
    setShowPasteConfimation(false);
    setPastedMember(null);
    return;
  };

  return (
    <div className={`${selected ? "" : ""}`}>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDeleteDialog}
        />
      )}

      {showPasteConfirmation && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("pasteMember")}
          onToggleDeleteDialog={handlePasteConfimation}
        />
      )}
      {memberDeleteModalVisible && (
        <>
          <Dialog
            title={"Delete Member"}
            width={"500px"}
            onClose={() => toggleMemberDeleteDialog()}
          >
            <p className="mb-2">
              Are you sure you want to un delete this member? Please select a
              reason to Delete.
            </p>

            <div>
              <DropDownList
                className="px-2 font-small z-[100010]"
                data={DeletionReasons}
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.value)}
              />
            </div>

            <DialogActionsBar>
              <div className="flex gap-x-2 justify-end">
                <Button
                  themeColor={"base"}
                  onClick={() => handleMemberDeleteDialog("no")}
                  className="iconcta"
                >
                  <span className="inline-flex items-center gap-1">
                    <X className="h-5 w-5" strokeWidth={1} />
                    No
                  </span>
                </Button>
                <Button
                  className="iconcta"
                  themeColor={"primary"}
                  disabled={!deletionReason}
                  onClick={() => handleMemberDeleteDialog("yes")}
                >
                  <span className="inline-flex items-center gap-1">
                    <CheckIcon className="h-5 w-5" strokeWidth={1} />
                    Yes
                  </span>
                </Button>
              </div>
            </DialogActionsBar>
          </Dialog>
        </>
      )}

      {unDeleteModalVisible && (
        <>
          <Dialog
            title={`Un Delete ${
              selectedLevel.current.level === 3 ? "BBS" : "Member"
            }`}
            width={"345px"}
            onClose={() => toggleUnDeleteDialog()}
          >
            <p className="mb-2">
              Are you sure you want to un delete this{" "}
              {selectedLevel.current.level === 3 ? "BBS" : "member"}?
            </p>
            <DialogActionsBar>
              <div className="flex gap-x-2 justify-end">
                <Button
                  themeColor={"base"}
                  onClick={() => handleUndeleteDialog("no")}
                  className="iconcta"
                >
                  <span className="inline-flex items-center gap-1">
                    <X className="h-5 w-5" strokeWidth={1} />
                    No
                  </span>
                </Button>
                <Button
                  className="iconcta"
                  themeColor={"primary"}
                  onClick={() => handleUndeleteDialog("yes")}
                >
                  <span className="inline-flex items-center gap-1">
                    <CheckIcon className="h-5 w-5" strokeWidth={1} />
                    Yes
                  </span>
                </Button>
              </div>
            </DialogActionsBar>
          </Dialog>
        </>
      )}

      {deletePermanentlyModalVisible && (
        <>
          <Dialog
            title={"Delete Permanently"}
            width={"400px"}
            onClose={() => toggleDeletePermanentlyDialog()}
          >
            <p className="mb-2">
              Are you sure you want to permanently delete this{" "}
              {selectedLevel.current.level === 3 ? "BBS" : "member"}? This
              action cannot be undone.
            </p>
            <DialogActionsBar>
              <div className="flex gap-x-2 justify-end">
                <Button
                  themeColor={"base"}
                  onClick={() => handleDeletePermanentlyDialog("no")}
                  className="iconcta"
                >
                  <span className="inline-flex items-center gap-1">
                    <X className="h-5 w-5" strokeWidth={1} />
                    Cancel
                  </span>
                </Button>
                <Button
                  className="iconcta"
                  themeColor={"error"}
                  onClick={() => handleDeletePermanentlyDialog("yes")}
                >
                  <span className="inline-flex items-center gap-1">
                    <Trash2Icon className="h-5 w-5" strokeWidth={1} />
                    Delete Permanently
                  </span>
                </Button>
              </div>
            </DialogActionsBar>
          </Dialog>
        </>
      )}

      {showPdfPreview && (
  <div className={`fixed h-screen w-screen z-[999999] top-0 left-0`}>
    <div
      className="overlay"
      onClick={() => setShowPdfPreview(false)}
    ></div>
    <div className="flex justify-center items-center w-full h-full">
      <iframe
        src={fileUrl}
        style={{ height: "100vh", width: "90vw", border: "none" }}
        title="PDF Preview"
      />
    </div>
  </div>
)}
      {!selected && (
        <>
          <div className="p-2 flex justify-between w-full gap-x-2 min-h-[48px] sticky top-0">
            <div className="flex-1 flex items-center gap-2">
              <SearchBox
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search.."
              />
              {isSearching && searchResults.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">
                    {currentSearchIndex + 1} of {searchResults.length}
                  </span>
                  <Button
                    title="Previous Result"
                    className="border border-[#E2E8F0] rounded-md p-1 h-8 w-8 flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      goToPreviousSearchResult();
                    }}
                  >
                    <ArrowLeft size={16} />
                  </Button>
                  <Button
                    title="Next Result"
                    className="border border-[#E2E8F0] rounded-md p-1 h-8 w-8 flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      goToNextSearchResult();
                    }}
                  >
                    <ArrowRight size={16} />
                  </Button>
                </div>
              )}
            </div>
            {isAuthorize([SUPER_USER]) && <Button
              title="Add New"
              className="border border-[#E2E8F0] rounded-md px-2 py-2 max-h-[40px]"
              onClick={() => {
                if (
                  commonState?.selectedProject?.event?.item?.level === 0 ||
                  !commonState?.selectedProject
                ) {
                  setShowAddProjectDrawer(!showAddProjectDrawer);
                } else if (
                  commonState?.selectedProject?.event?.item?.level != 3 &&
                  commonState?.selectedProject?.event?.item?.level != 2
                ) {
                  addLevel(selectedLevel.current, "new");
                }
              }}
            >
              <Plus className="text-[6px] text-[#484F61]" size="20" />
            </Button>}
            <div className="flex gap-x-1 max-h-[40px]">
              <Button
                title="Collapse All"
                className="border border-[#E2E8F0] rounded-md px-2 py-2"
                onClick={handleCollapseAll}
              >
                <CopyMinus size="20" />
              </Button>
              <Button
                title="Expand All"
                className="border border-[#E2E8F0] rounded-md px-2 py-2"
                onClick={handleExpandAll}
              >
                <CopyPlus size="20" />
              </Button>
            </div>
          </div>
        </>
      )}
      {/* {selected && (
          <>
            <Button
              className="border border-[#E2E8F0] rounded-md px-2 py-0 max-h-[40px]"
              onClick={() => {
                if (
                  commonState?.selectedProject?.event?.item?.level === 0 ||
                  !commonState?.selectedProject
                ) {
                  setShowAddProjectDrawer(!showAddProjectDrawer);
                } else {
                  addLevel(selectedLevel.current, "new");
                }
              }}
            >
              <Plus className="text-[6px] text-[#484F61]" size={20} />
            </Button>
          </>
        )} */}

      {!selected && (
        <>
          <hr className="ml-[10px]" />
          <div
            className={`w-full mt-1 tree-content  ${
              showManageBBS || showManageCallOff
                ? "h-[45vh!important] overflow-y-scroll"
                : "h-full overflow-y-auto"
            } ${commonState?.selected ? "" : ""}`}
          >
            <AddLevelContext.Provider
              value={{
                // Project Right Click Functions //

                addLevel: (data: any, action: string) => addLevel(data, action),
                handleManageProject: (data: any, action: string) =>
                  handleManageProject(data, action),
                handleSummary: (data: any, action: string) =>
                  handleSummary(data, action),
                handleProjectLogs: (data: any) => handleProjectLogs(data),
                handleManageDetailingOffices: (data: any, action: string) =>
                  handleManageDetailingOffices(data, action),
                handleManageSubcontractors: (data: any, action: string) =>
                  handleManageSubcontractors(data, action),
                // Project Right Click Functions //

                // Member Right Click Functions //
                handleManageMemberRevision: (data: any, action: string) =>
                  handleManageMemberRevision(data, action),
                handleManageMemberProperties: (data: any, action: string) =>
                  handleManageMemberProperties(data, action),
                handleManageMemberImport: (data: any, action: string) =>
                  handleManageMemberImport(data, action),
                handleMemberPDFExport: (data: any, action: string) =>
                  handleMemberPDFExport(data, action),
                handleMemberExcelExport: (data: any, action: string) =>
                  handleMemberExcelExport(data, action),
                handleMemberAssignment: (data: any, action: string) =>
                  handleMemberAssignment(data, action),
                handleMemberParts: (data: any, action: string) =>
                  handleMemberParts(data, action),
                handleCopyMember: (data: any, action: string) =>
                  handleCopyMember(data, action),
                handlePasteMember: (data: any, action: string) =>
                  handlePasteMember(data, action),
                // Member Right Click Functions //

                // Common Right Click Functions //
                handleManageBBS: (data: any, action: string) =>
                  handleManageBBS(data, action),
                handleManageCallOff: (data: any, action: string) =>
                  handleManageCallOff(data, action),

                // Common Right Click Functions //

                // Level Right Click Functions //

                handleCopyUnitsClick: (data: any, action: string) =>
                  handleCopyUnitsClick(data, action),

                handlePasteUnitsClick: (data: any, action: string) =>
                  handlePasteUnitsClick(data, action),

                handleDeleteUnitsClick: (data: any, action: string) =>
                  handleDeleteUnitsClick(data, action),
                // Level Right Click Functions //
                toggleMemberDeleteDialog: () => toggleMemberDeleteDialog(),
                toggleUnDeleteDialog: () => toggleUnDeleteDialog(),
                toggleDeletePermanentlyDialog: () =>
                  toggleDeletePermanentlyDialog(),
                setSelectedItem: (data: any) => setSelectedItem(data),
                handleTreeSelection: handleTreeSelection,
                showManageBBS: showManageBBS,
                showManageCallOff: showManageCallOff,
                isMemberCopied: isMemberCopied,
                searchValue: searchValue,
                highlightMatchedText: (text: string, searchValue: string) =>
                  highlightMatchedText(text, searchValue),
              }}
            >
              <TreeView
                data={processTreeViewItems(filteredTreeView, {
                  select: select,
                  check: check,
                  expand: expand,
                })}
                className={`${
                  showManageBBS || showManageCallOff
                    ? "overflow-y-hidden"
                    : "overflow-y-auto"
                }`}
                size={"small"}
                expandIcons={true}
                onExpandChange={onExpandChange}
                // aria-multiselectable={true}
                onItemClick={onItemClick}
                checkboxes={showCheckboxes()}
                disableField="isDisabled"
                onCheckChange={onCheckChange}
                item={TreeViewItemCell}
              />
            </AddLevelContext.Provider>
            {showDialog && (
              <Dialog
                width={500}
                title={getModalHeaderText()}
                onClose={() => setShowDialog(false)}
                className="custom-dialog"
              >
                <div className="my-4">
                  <CustomLabel
                    label={getModalHeaderText()?.split(" ")[1] || ""}
                    required={true}
                  />
                  <Input
                    type="text"
                    placeholder={"Enter Name"}
                    value={inputVal}
                    onChange={(e: any) => {
                      setInputVal(e.target.value);
                    }}
                    autoComplete="new-password"
                    list="autoCompleteOff"
                  />
                </div>
                <DialogActionsBar>
                  <div className="flex justify-end gap-3">
                    <Button
                      className="border border-[#E2E8F0] xpx-2 py-2 font-small"
                      themeColor={"primary"}
                      disabled={!inputVal}
                      onClick={handleSaveLevel}
                    >
                      Save
                    </Button>
                    <Button
                      className="border border-[#E2E8F0]"
                      onClick={() => {
                        setInputVal("");
                        setShowDialog(false);
                      }}
                      themeColor={"base"}
                      fillMode="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogActionsBar>
              </Dialog>
            )}
          </div>
        </>
      )}

      <ProjectAuditLogsDrawer
        showAuditLogsDrawer={showAuditLogsDrawer}
        setShowAuditLogsDrawer={setShowAuditLogsDrawer}
        selectedProject={selectedProjectForAuditLogs}
      />
    </div>
  );
};

export default TreeViewMain;

const TreeViewItemCell: React.FC<ItemRenderProps> = (props) => {
  const {
    addLevel,
    handleManageProject,
    handleSummary,
    handleProjectLogs,
    handleManageDetailingOffices,
    handleManageSubcontractors,
    handleManageMemberRevision,
    handleManageMemberProperties,
    handleManageMemberImport,
    handleMemberPDFExport,
    handleMemberExcelExport,
    handleMemberAssignment,
    handleMemberParts,
    handleCopyMember,
    handlePasteMember,
    handleManageBBS,
    handleManageCallOff,
    handleCopyUnitsClick,
    handleDeleteUnitsClick,
    handlePasteUnitsClick,
    toggleUnDeleteDialog,
    toggleDeletePermanentlyDialog,
    setSelectedItem,
    showManageBBS,
    showManageCallOff,
    isMemberCopied,
    toggleMemberDeleteDialog,
    handleTreeSelection,
    searchValue,
    highlightMatchedText,
  } = useContext(AddLevelContext);
  const anchor = useRef<HTMLButtonElement>(null);

  const [show, setShow] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: any) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setShow(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        anchor.current &&
        !anchor.current.contains(event.target)
      ) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const onClick = useCallback((data: any) => {
    setShow((prevShow) => !prevShow);
    setSelectedItem(data);
  }, []);

  const openDialog = (action: string) => {
    addLevel(props.item, action);
  };
  const unDeleteClick = () => {
    toggleUnDeleteDialog();
  };

  const openMemberDeleteDialog = () => {
    toggleMemberDeleteDialog();
  };

  const canDelete = () => {
    if(!isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR])) return false;
    if (props.item.level === 0) return false;
    if (props.item.level !== 4) {
      return true;
    }

    if (props.item.level === 4 || props.item.level === 5) {
      if (props.item.status === "Released") return false;
    }
    // if (props.item.level === 2) {
    //   if (props.item.items && props.item.items.length > 0) {
    //     return false;
    //   }
    // }

    return true;
  };

  const canPerformMemberActions = () => {
    if(!isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR])) return false;
    if (props.item.level === 4 || props.item.level === 5) {
      if (props.item.status === "Released") return false;
    }
    return true;
  };

  const hideActions = () => {
    if (isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR])) return false;
    if (props.item.level === 2 || props.item.level === 3) return true;
  }

  return (
    <>
      <div
        className={`text-left w-full ${
          props.item.level === 3 && props.item.status === "Released" ? "released" :
          props.item.level !== 0 && !props.item.isActive ? "inactive" : ""
        } ${
          (props.item.level === 4 || props.item.level === 5) &&
          props.item.status === "Released"
            ? "released"
            : props.item.status === "Partially Released"
            ? "partially-released"
            : props.item.status === "Called Off"
            ? "called-off"
            : props.item.status === "Partially Called Off"
            ? "partially-called-off"
            : ""
        }
        `}
        data-unique-id={props.item.uniqueId}
      >
        <div className="flex items-center gap-x-2 group flex-1 w-full min-w-0">
          <div
            className={`self-center ${
              showManageBBS && props.item.level === 4 ? "" : "cursor-pointer"
            } hover:text-primaryMain flex items-center gap-1 flex-1 project-text min-w-0`}
            // style={props.item.level === 5 ? { marginLeft: "-1.5rem" } : {}}
          >
            {props.item.level === 0 && (
              <Folder height={14} className="text-primaryMain flex-shrink-0" />
            )}
            {props.item.level === 1 && (
              <FolderGit height={14} className="text-primaryMain flex-shrink-0" />
            )}
            {props.item.level === 2 && (
              <FolderGit2 height={14} className="text-primaryMain flex-shrink-0" />
            )}
            {props.item.level === 3 && (
              <FolderGit2 height={14} className="text-primaryMain flex-shrink-0" />
            )}
            {props.item.level === 4 && (
              <List height={14} className="text-primaryMain flex-shrink-0" />
            )}
            {props.item.level === 5 && (
              <Component height={12} className="text-primaryMain flex-shrink-0" />
            )}
            <span className="overflow-hidden text-ellipsis whitespace-break-spaces flex flex-wrap max-w-[284px]">
              {props.item.level === 4 || props.item.level === 5
                ? highlightMatchedText(props.item.text, searchValue)
                : highlightMatchedText(
                    props.item.text?.split("-Code-")[0] || "",
                    searchValue
                  )}
            </span>
          </div>
          {!showManageBBS && !showManageCallOff && !hideActions() && (
            <div className="text-left h-5">
              <>
                <button
                  className={`${"opacity-100 visible"}  cta_dropdown  transition-all duration-300 ${
                    show ? "active" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onClick(props.item);
                  }}
                  ref={anchor}
                >
                  <MoreHorizontal strokeWidth={1} size={22} />
                </button>
                <Popup
                  anchor={anchor.current}
                  show={show}
                  popupClass={"max-w-[300px]"}
                  anchorAlign={{ horizontal: "right", vertical: "bottom" }}
                  popupAlign={{ horizontal: "right", vertical: "top" }}
                >
                  <div ref={popupRef} onClick={handleTreeSelection}>
                    {props.item.level === 0 && (
                      <React.Fragment>
                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={() => {
                            setShow(!show);
                            handleManageProject(props.item, "save");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <FolderKanban className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Project
                            </span>
                          </span>
                        </Button>}
                        {/* <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageBBS(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Layers className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage BBS
                            </span>
                          </span>
                        </Button> */}
                        {<Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleManageCallOff(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BringToFront className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Call-Off
                            </span>
                          </span>
                        </Button>}
                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={() => {
                            handleManageDetailingOffices(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BriefcaseIcon className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Detailing Offices
                            </span>
                          </span>
                        </Button>}
                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={() => {
                            handleManageSubcontractors(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <User className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Subcontractors
                            </span>
                          </span>
                        </Button>}

                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("new");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <CopyPlus className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              New Level
                            </span>
                          </span>
                        </Button>}
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShow(!show);
                            handleSummary(props.item, "BBS");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              BBS Summary
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShow(!show);
                            handleSummary(props.item, "Revised Summary");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Revised Bar Marks Summary
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShow(!show);
                            handleSummary(props.item, "CallOff");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Call-Off Summary
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShow(!show);
                            handleProjectLogs(props.item);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <History className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Logs
                            </span>
                          </span>
                        </Button>
                      </React.Fragment>
                    )}

                    {props.item.level === 1 && (
                      <>
                        {/*<Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageBBS(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Layers className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage BBS
                            </span>
                          </span>
                        </Button>
                         <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageCallOff(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BringToFront className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Call-Off
                            </span>
                          </span>
                        </Button> */}
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShow(!show);
                            handleSummary(props.item, "CallOff");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Call-Off Summary
                            </span>
                          </span>
                        </Button>
                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("newChild");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <CopyPlus className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              New Element
                            </span>
                          </span>
                        </Button>}
                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("edit");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <SquarePen className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Rename
                            </span>
                          </span>
                        </Button>}
                        {canDelete() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="solid"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openDialog("delete");
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Delete
                                </span>
                              </span>
                            </Button>
                            {!props.item.isActive && (
                              <Button
                                className="w-full iconBtn px-1"
                                fillMode="flat"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setShow(!show);
                                  setSelectedItem(props.item);
                                  toggleUnDeleteDialog();
                                }}
                              >
                                <span className="inline-flex items-center gap-2 w-full">
                                  <Trash2Icon className="w-4 h-4" />
                                  <span className="flex-1 text-xs font-normal">
                                    Un-Delete
                                  </span>
                                </span>
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {props.item.level === 2 && (
                      <>
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleManageBBS(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Layers className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage BBS
                            </span>
                          </span>
                        </Button>
                        {/*<Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageCallOff(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BringToFront className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Call-Off
                            </span>
                          </span>
                        </Button> */}
                        {/* <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("newSubChild");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <CopyPlus className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              New Drawing
                            </span>
                          </span>
                        </Button> */}
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("edit");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <SquarePen className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Rename
                            </span>
                          </span>
                        </Button>
                        {canDelete() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="solid"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openDialog("delete");
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Delete
                                </span>
                              </span>
                            </Button>
                            {!props.item.isActive && (
                              <Button
                                className="w-full iconBtn px-1"
                                fillMode="flat"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setShow(!show);
                                  setSelectedItem(props.item);
                                  toggleUnDeleteDialog();
                                }}
                              >
                                <span className="inline-flex items-center gap-2 w-full">
                                  <Trash2Icon className="w-4 h-4" />
                                  <span className="flex-1 text-xs font-normal">
                                    Un-Delete
                                  </span>
                                </span>
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {props.item.level === 3 && (
                      <>
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={() => {
                            handleManageMemberProperties(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <CopyPlus className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              New Member
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20 pasteBtn"
                          fillMode="flat"
                          onClick={() => {
                            handlePasteMember(props.item, "paste");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <CopyPlus className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Paste Member
                            </span>
                          </span>
                        </Button>
                        {/*<Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageBBS(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Layers className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage BBS
                            </span>
                          </span>
                        </Button>
                         <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageCallOff(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BringToFront className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Call-Off
                            </span>
                          </span>
                        </Button> */}
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShow(!show);
                            openDialog("edit");
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <SquarePen className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Rename
                            </span>
                          </span>
                        </Button>
                        {canDelete() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="solid"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openDialog("delete");
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Delete
                                </span>
                              </span>
                            </Button>
                            {!props.item.isActive && (
                              <>
                                <Button
                                  className="w-full iconBtn px-1"
                                  fillMode="flat"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setShow(!show);
                                    setSelectedItem(props.item);
                                    toggleUnDeleteDialog();
                                  }}
                                >
                                  <span className="inline-flex items-center gap-2 w-full">
                                    <Trash2Icon className="w-4 h-4" />
                                    <span className="flex-1 text-xs font-normal">
                                      Un-Delete
                                    </span>
                                  </span>
                                </Button>
                                {hasProjectDeletionPermission(
                                  props?.item?.projectId
                                ) && isAuthorize([PROJECT_ADMIN]) && (
                                  <Button
                                    className="w-full iconBtn px-1 border-b border-neutral-20"
                                    fillMode="solid"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setShow(!show);
                                      setSelectedItem(props.item);
                                      toggleDeletePermanentlyDialog();
                                    }}
                                  >
                                    <span className="inline-flex items-center gap-2 w-full">
                                      <Trash2Icon className="w-4 h-4 text-red-600" />
                                      <span className="flex-1 text-xs font-normal text-red-600">
                                        Delete Permanently
                                      </span>
                                    </span>
                                  </Button>
                                )}
                              </>
                            )}
                          </>
                        )}
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleManageMemberImport(props.item, "member");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Import className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Import Member from Excel
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleManageMemberImport(props.item, "multiple");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Import className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Import multiple Members from Excel
                            </span>
                          </span>
                        </Button>
                        {/* <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageMemberImport(props.item, "revision");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Import className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Import multiple new Members revisions from Excel
                            </span>
                          </span>
                        </Button> */}
                      </>
                    )}

                    {props.item.level === 4 && (
                      <>
                        {canPerformMemberActions() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1"
                              fillMode="flat"
                              disabled={!props.item.isActive}
                              onClick={() => {
                                handleManageMemberRevision(props.item, "save");
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <SquarePlus className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Create New Member Revision
                                </span>
                              </span>
                            </Button>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="flat"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                // event.stopPropagation();
                                handleMemberParts(props.item, "manage");
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Ungroup className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Manage Member Parts
                                </span>
                              </span>
                            </Button>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="flat"
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openDialog("edit");
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <SquarePen className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Rename
                                </span>
                              </span>
                            </Button>
                          </>
                        )}

                        {/* <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageBBS(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Layers className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage BBS
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageCallOff(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <BringToFront className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Manage Call-Off
                            </span>
                          </span>
                        </Button> */}
                        <Button
                          className="w-full iconBtn px-1  border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMemberExcelExport(
                              {
                                id: props.item.id,
                                name: props.item.text,
                              },
                              "save"
                            );
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Export to Excel
                            </span>
                          </span>
                        </Button>
                        {canPerformMemberActions() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1"
                              fillMode="flat"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleManageMemberImport(props.item, "units");
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Import className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Import Member Units from Excel
                                </span>
                              </span>
                            </Button>
                            {/* <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="flat"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleManageMemberImport(
                                  props.item,
                                  "revision"
                                );
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Import className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Import new Member revision from Excel
                                </span>
                              </span>
                            </Button> */}
                          </>
                        )}
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMemberAssignment(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Paperclip className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Show BBS\Call-Offs member used in
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMemberPDFExport(
                              {
                                id: props.item.id,
                                name: props.item.text,
                              },
                              "save"
                            );
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <FileText className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Generate Report
                            </span>
                          </span>
                        </Button>

                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1 copyBtn"
                          fillMode="flat"
                          disabled={!props.item.isActive}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyMember(props.item, "copy");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Copy className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Copy Member
                            </span>
                          </span>
                        </Button>}

                        {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          disabled={!props.item.isActive}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyUnitsClick(props.item, "copy");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Copy className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Copy Units
                            </span>
                          </span>
                        </Button>}
                        {canPerformMemberActions() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1"
                              fillMode="flat"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePasteUnitsClick(props.item, "paste");
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <ClipboardPaste className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Paste Units
                                </span>
                              </span>
                            </Button>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="flat"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteUnitsClick(props.item, "delete");
                                setShow(!show);
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Delete Units
                                </span>
                              </span>
                            </Button>
                          </>
                        )}
                        {canDelete() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1"
                              fillMode="flat"
                              disabled={!props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openMemberDeleteDialog();
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Delete
                                </span>
                              </span>
                            </Button>

                            <Button
                              className="w-full iconBtn px-1"
                              fillMode="flat"
                              disabled={props.item.isActive}
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                unDeleteClick();
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <Trash2Icon className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Un-Delete
                                </span>
                              </span>
                            </Button>

                            {!props.item.isActive &&
                              hasProjectDeletionPermission(
                                props?.item?.projectId
                              ) && isAuthorize([PROJECT_ADMIN]) && (
                                <Button
                                  className="w-full iconBtn px-1"
                                  fillMode="flat"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setShow(!show);
                                    setSelectedItem(props.item);
                                    toggleDeletePermanentlyDialog();
                                  }}
                                >
                                  <span className="inline-flex items-center gap-2 w-full">
                                    <Trash2Icon className="w-4 h-4 text-red-600" />
                                    <span className="flex-1 text-xs font-normal text-red-600">
                                      Delete Permanently
                                    </span>
                                  </span>
                                </Button>
                              )}
                          </>
                        )}

                        <Button
                          className="w-full iconBtn px-1"
                          fillMode="flat"
                          disabled={!props.item.isActive}
                          onClick={(e) => {
                            // e.stopPropagation();
                            handleManageMemberProperties(props.item, "save");
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <TableProperties className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Properties
                            </span>
                          </span>
                        </Button>
                      </>
                    )}
                    {props.item.level === 5 && (
                      <>
                        {canPerformMemberActions() && (
                          <>
                            <Button
                              className="w-full iconBtn px-1 border-b border-neutral-20"
                              fillMode="flat"
                              onClick={(event) => {
                                event.stopPropagation();
                                setShow(!show);
                                openDialog("edit");
                              }}
                            >
                              <span className="inline-flex items-center gap-2 w-full">
                                <SquarePen className="w-4 h-4" />
                                <span className="flex-1 text-xs font-normal">
                                  Rename
                                </span>
                              </span>
                            </Button>
                          </>
                        )}
                        <Button
                          className="w-full iconBtn px-1 border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleMemberPDFExport(
                              {
                                id: props.item.id,
                                name: props.item.text,
                              },
                              "save"
                            );
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <FileText className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Generate Report
                            </span>
                          </span>
                        </Button>
                        <Button
                          className="w-full iconBtn px-1  border-b border-neutral-20"
                          fillMode="flat"
                          onClick={(event) => {
                            // event.stopPropagation();
                            handleMemberExcelExport(
                              {
                                id: props.item.id,
                                name: props.item.text,
                              },
                              "save"
                            );
                            setShow(!show);
                          }}
                        >
                          <span className="inline-flex items-center gap-2 w-full">
                            <Download className="w-4 h-4" />
                            <span className="flex-1 text-xs font-normal">
                              Export to Excel
                            </span>
                          </span>
                        </Button>
                      </>
                    )}
                  </div>
                </Popup>
              </>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
